import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useStore } from '../hooks/useStore';
import { fetchOverview } from '../lib/api';
import { safeArray } from '../lib/safe';

export const Route = createFileRoute('/')({
  component: OverviewPage,
});

const TIME_RANGES = [
  { label: '7 days', value: '7' },
  { label: '30 days', value: '30' },
  { label: '90 days', value: '90' },
  { label: 'All time', value: '9999' },
];

// ── Stat Card ────────────────────────────────────────
function StatCard({ label, value, color, sub }: { label: string; value: string | number; color: string; sub?: string }) {
  return (
    <div className="bg-white rounded-xl border border-[var(--ccb-border)] p-5 flex flex-col gap-1">
      <span className="text-xs font-medium text-[var(--ccb-text-secondary)] uppercase tracking-wide">{label}</span>
      <span className={`text-2xl font-bold ${color}`}>{value}</span>
      {sub && <span className="text-xs text-[var(--ccb-text-secondary)]">{sub}</span>}
    </div>
  );
}

// ── Overview Page ────────────────────────────────────
function OverviewPage() {
  const { activeStore } = useStore();
  const [days, setDays] = useState('30');

  const { data, isLoading, error } = useQuery({
    queryKey: ['overview', activeStore.shop, days],
    queryFn: () => fetchOverview(activeStore.shop, days),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-[var(--ccb-text)]">Dashboard Overview</h2>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-[var(--ccb-border)] p-5 h-24 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
        <p className="font-medium">Failed to load dashboard data</p>
        <p className="text-sm mt-1">{(error as Error).message}</p>
      </div>
    );
  }

  const stats = data?.stats;
  // Always ensure arrays — even if API returns something unexpected
  const vendors = safeArray(data?.vendors);
  const errors = safeArray(data?.recentErrors);
  // Stats from optimizer: {total, completed, failed, processing, pending}
  const completedPct = stats && stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-[var(--ccb-text)]">Dashboard Overview</h2>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {TIME_RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                days === r.value
                  ? 'bg-white text-[var(--ccb-text)] shadow-sm'
                  : 'text-[var(--ccb-text-secondary)] hover:text-[var(--ccb-text)]'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Jobs" value={stats?.total ?? '—'} color="text-[var(--ccb-text)]" sub={days === '9999' ? 'All time' : `Last ${days} days`} />
        <StatCard label="Completed" value={stats?.completed ?? '—'} color="text-[var(--ccb-success)]" sub={`${completedPct}% success`} />
        <StatCard label="Processing" value={(stats?.processing ?? 0) + (stats?.pending ?? 0)} color="text-[var(--ccb-warning)]" sub="In queue" />
        <StatCard label="Failed" value={stats?.failed ?? '—'} color={stats?.failed ? 'text-[var(--ccb-error)]' : 'text-[var(--ccb-text)]'} />
      </div>

      {/* Two‑column: Vendors + Recent Errors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Vendors */}
        <div className="bg-white rounded-xl border border-[var(--ccb-border)] p-5">
          <h3 className="text-sm font-semibold text-[var(--ccb-text)] mb-4">Top Vendors</h3>
          {vendors.length === 0 ? (
            <p className="text-sm text-[var(--ccb-text-secondary)]">No vendor data available</p>
          ) : (
            <div className="space-y-3">
              {vendors.slice(0, 8).map((v: any) => (
                <div key={v.vendor} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-[var(--ccb-text)] truncate mr-3">{v.vendor}</span>
                  <span className="text-[var(--ccb-text-secondary)] shrink-0">{v.count} products</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Errors */}
        <div className="bg-white rounded-xl border border-[var(--ccb-border)] p-5">
          <h3 className="text-sm font-semibold text-[var(--ccb-text)] mb-4">Recent Errors</h3>
          {errors.length === 0 ? (
            <p className="text-sm text-[var(--ccb-text-secondary)]">No recent errors</p>
          ) : (
            <div className="space-y-3">
              {errors.slice(0, 6).map((err) => (
                <div key={err.id} className="flex items-start gap-3 text-sm">
                  <span className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${err.resolved ? 'bg-gray-300' : 'bg-red-500'}`} />
                  <div className="min-w-0">
                    <p className="font-medium text-[var(--ccb-text)] truncate">{err.title}</p>
                    <p className="text-xs text-[var(--ccb-text-secondary)] truncate">{err.error}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
